<!--
  @component

  The text field component

  ```html
  <oc-text-field-v1 name="firstname" placeholder="Max">Your first name</oc-text-field-v1>
  ```

  @slot required `default` Sets a label for the text field. Use it to provide a brief description of the text field. This help users to understand its purpose.
-->
<svelte:options
  customElement={{
    tag: "oc-text-field-v1",
    shadow: "none",
    /*                                                         */
    /*         */
    extend: (CustomElementConstructor) =>
      class extends CustomElementConstructor {
        /*                                          */
        static formAssociated = true;

        constructor() {
          super();
          this.attachShadow({ mode: "open", delegatesFocus: true });

          /*                                */
          this.internals = this.attachInternals();

          this.setValidity = (...args) => this.internals.setValidity(...args);
        }

        /*                                                            */
        get willValidate() {
          return this.internals.willValidate;
        }

        get validity() {
          return this.internals.validity;
        }

        get validationMessage() {
          return this.internals.validationMessage;
        }

        get form() {
          return this.internals.form;
        }

        /*                       */
        formResetCallback() {
          /*                                   */
          this.value = this.getAttribute("value") || "";
        }
      },
    props: {
      type: { type: "String" },
      placeholder: { type: "String" },
      name: { type: "String", reflect: true }, /*                                    */
      value: { type: "String" }, /*                                     */
      label: { type: "String" },
      hint: { type: "String" },
      pattern: { type: "String" },
      variant: { type: "String" },
      autocomplete: { type: "String" },
      required: { type: "Boolean" },
      minlength: { type: "Number" },
      maxlength: { type: "Number" },
      prefixText: { type: "String", attribute: "prefix-text" },
      suffixText: { type: "String", attribute: "suffix-text" },
      prefixIcon: { type: "String", attribute: "prefix-icon" },
      suffixIcon: { type: "String", attribute: "suffix-icon" },
      passwordVisible: { type: "String", attribute: "password-visible" },
      disabled: { type: "Boolean", reflect: true }, /*                                    */
    },
  }}
/>

<script lang="ts">
  import type { IconType } from "@otto-ec/icons";
  import HintAndCounterV1 from "../../../../common/components/HintAndCounterV1.svelte";
  import { usePropertyChange } from "../../../../common/utils/usePropertyChange";

  usePropertyChange();

  /**
 *
 */
  export let name: string;

  /**
 *
 *
 *
 *
 *
 *
 */
  export let value: string = "";

  /**
 *
 */
  export let hint: string | undefined = undefined;

  /**
 *
 *
 *
 */
  export let variant: "default" | "error" | "success" | "warning" = "default";

  /**
 *
 *
 *
 */
  export let required: boolean = false;

  /**
 *
 *
 *
 */
  export let disabled: boolean = false;

  /**
 *
 *
 *
 */
  export let pattern: string | undefined = undefined;

  /**
 *
 *
 *
 *
 */
  export let autocomplete:
    | "on"
    | "off"
    | "address-line1"
    | "address-line2"
    | "address-line3"
    | "address-level1"
    | "address-level2"
    | "address-level3"
    | "address-level4"
    | "street-address"
    | "country"
    | "country-name"
    | "postal-code"
    | "name"
    | "additional-name"
    | "family-name"
    | "give-name"
    | "honoric-prefix"
    | "honoric-suffix"
    | "nickname"
    | "organization-title"
    | "username"
    | "new-password"
    | "current-password"
    | "bday"
    | "bday-day"
    | "bday-month"
    | "bday-year"
    | "sex"
    | "one-time-code"
    | "organization"
    | "cc-name"
    | "cc-given-name"
    | "cc-additional-name"
    | "cc-family-name"
    | "cc-number"
    | "cc-exp"
    | "cc-exp-month"
    | "cc-exp-year"
    | "cc-csc"
    | "cc-type"
    | "transaction-currency"
    | "transaction-amount"
    | "language"
    | "url"
    | "email"
    | "photo"
    | "tel"
    | "tel-country-code"
    | "tel-national"
    | "tel-area-code"
    | "tel-local"
    | "tel-local-prefix"
    | "tel-local-suffix"
    | "tel-extension"
    | "impp" = "off";

  /**
 *
 *
 *
 */
  export let placeholder: string | undefined = undefined;
  /*                                                                          */
  $: nonEmptyPlaceholder = placeholder || " ";

  /*                                                    */
  /**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
  /*                                                                                                         */
  export let type:
    | "email"
    | "number"
    | "password"
    | "tel"
    | "text"
    | "url"
    | "integer"
    | "decimal" = "text";
  /*                             */
  $: nativeInputType = ["integer", "decimal"].includes(type) ? "number" : type;

  /**
 *
 *
 *
 */
  export let minlength: number | undefined = undefined;

  /**
 *
 *
 *
 */
  export let maxlength: number | undefined = undefined;

  /**
 *
 */
  export let prefixText: string | undefined = undefined;

  /**
 *
 */
  export let suffixText: string | undefined = undefined;

  /**
 *
 */
  export let prefixIcon: IconType | undefined = undefined;

  /**
 *
 */
  export let suffixIcon: IconType | undefined = undefined;

  /**
 *
 *
 *
 */
  export let passwordVisible: boolean = false;

  /*            */
  export let internals: ElementInternals;

  let inputElement: HTMLInputElement;

  $: if (inputElement) {
    internals.setFormValue(value);
    if (!internals?.form?.noValidate) {
      internals.setValidity(inputElement.validity, inputElement.validationMessage, inputElement);
    }
  }

  /*                                    */
  const onInput = () => {
    value = inputElement.value;
  };

  /*               */
  const focus = ({ target }: Event) => {
    if (target?.tagName !== "BUTTON") {
      inputElement?.focus();
    }
  };

  /*                                                      */
  const selectText = ({ target }: Event) => {
    if (target?.tagName !== "BUTTON") {
      inputElement?.select();
    }
  };

  /*                                          */
  const beforeInput = (event: InputEvent) => {
    if (event.data) {
      if (type === "decimal" && !/^[0-9.,]*$/.test(event.data)) {
        event.preventDefault();
      } else if (type === "integer" && !/^\d*$/.test(event.data)) {
        event.preventDefault();
      }
    }
  };

  /*                                       */
  $: ariaDescribedById = hint && "hint";
</script>

<div
  class="text-field"
  class:text-field--error={variant === "error"}
  class:text-field--warning={variant === "warning"}
  class:text-field--success={variant === "success"}
  class:text-field--disabled={disabled}
  class:text-field--has-value={value}
  role="none"
  on:click={focus}
  on:dblclick={selectText}
>
  <input
    class="text-field__input"
    id="input"
    bind:this={inputElement}
    {value}
    type={passwordVisible ? "text" : nativeInputType}
    placeholder={nonEmptyPlaceholder}
    {required}
    {disabled}
    {name}
    {pattern}
    {minlength}
    {maxlength}
    {autocomplete}
    aria-describedby={ariaDescribedById}
    on:beforeinput={beforeInput}
    on:input={onInput}
  />

  {#if prefixText}
    <small class="text-field__prefix-text">{prefixText}</small>
  {/if}

  {#if prefixIcon}
    <oc-icon-v1 class="text-field__prefix-icon" type={prefixIcon} />
  {/if}

  {#if suffixText}
    <small class="text-field__suffix-text">{suffixText}</small>
  {/if}

  {#if suffixIcon}
    <oc-icon-v1 class="text-field__suffix-icon" type={suffixIcon} />
  {/if}

  {#if type === "password"}
    <button
      class="text-field__password-toggle"
      on:click={() => {
        passwordVisible = !passwordVisible;
      }}
      role="switch"
      aria-checked={passwordVisible}
      aria-label={passwordVisible ? "Passwort ausblenden" : "Passwort anzeigen"}
      >{passwordVisible ? "Ausblenden" : "Anzeigen"}</button
    >
  {/if}

  <label class="text-field__label" for="input"><slot /></label>
</div>

<HintAndCounterV1
  hintId="hint"
  {variant}
  {hint}
  {disabled}
  minCounter={minlength}
  maxCounter={maxlength}
  counterValue={value}
/>

<style lang="scss">
  @use "TextFieldV1.config" as config;
  @use "@otto-ec/design-tokens/tokens";
  @use "../../../../common/scss/focus.scss";

  $autofill-background-color: #f0f4fc;
  $transition: all 0.1s cubic-bezier(0.4, 0, 0.2, 1);
  $gap: 8px;

  :host {
    @include config.oc-text-field;
  }

  :host[minlengt],
  :host[maxlength],
  :host[hint] {
    @include config.oc-text-field-hint;
  }

  .text-field {
    box-sizing: border-box;
    position: relative;
    display: grid;
    grid-template-areas: "prefix input suffix";
    grid-template-columns: auto 1fr auto;
    align-items: center;

    padding: tokens.$oc-component-form-field-padding-y tokens.$oc-component-form-field-padding-x;

    background: tokens.$oc-component-form-background-color;
    outline: 1px solid tokens.$oc-component-form-default-border-color;
    border-radius: tokens.$oc-component-form-field-border-radius;
    cursor: text;

    &:hover:not(.text-field--disabled) {
      outline-width: 2px;
    }

    &:focus-within {
      outline-width: 2px;
      &:not(.text-field--error):not(.text-field--warning):not(.text-field--success) {
        outline-color: tokens.$oc-component-form-default-border-color-focus;
      }
    }

    &__input {
      all: unset;
      grid-area: input;

      width: 100%;

      font: tokens.$oc-component-form-field-input-font;
      color: tokens.$oc-component-form-field-input-color;

      &::placeholder {
        font: tokens.$oc-component-form-field-placeholder-font;
        color: tokens.$oc-component-form-field-placeholder-color;
        opacity: 0;
        transition: $transition;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 100%;
        box-sizing: border-box;
      }
    }

    &__label {
      position: absolute;
      top: tokens.$oc-component-form-field-padding-y;
      left: tokens.$oc-semantic-spacing-50;
      padding: 0 tokens.$oc-semantic-spacing-25;
      max-width: calc(100% - 2 * tokens.$oc-semantic-spacing-50);
      box-sizing: border-box;

      transition: $transition;

      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      user-select: none;
      cursor: text;
      font: tokens.$oc-component-form-label-font;
      background-color: tokens.$oc-component-form-background-color;
      border-radius: tokens.$oc-component-form-field-label-border-radius;
      color: tokens.$oc-component-form-default-label-color;
    }

    /*                        */
    &__password-toggle {
      @include focus.styles(tokens.$oc-semantic-focus-outline-radius-12);
      position: relative;

      grid-area: suffix;
      margin-left: $gap;

      border: unset;
      background: unset;
      padding: unset;

      color: tokens.$oc-semantic-color-text-secondary;
      font: tokens.$oc-semantic-font-copy-75;

      &:hover {
        text-decoration: underline;
        color: tokens.$oc-base-color-black-lighten-23;
      }

      &:active {
        color: tokens.$oc-base-color-black-lighten-13;
      }

      &:focus {
        outline: unset;
      }

      /*                                                */
      &:before {
        content: "";
        position: absolute;
        top: -4px;
        bottom: -4px;
        left: -4px;
        right: -4px;
        background-color: transparent;
      }
    }

    &--error {
      outline-color: tokens.$oc-component-form-error-border-color;

      .text-field__label {
        color: tokens.$oc-component-form-error-label-color;
      }
    }

    &--warning {
      outline-color: tokens.$oc-component-form-warning-border-color;
    }

    &--success {
      outline-color: tokens.$oc-component-form-success-border-color;

      .text-field__label {
        color: tokens.$oc-component-form-success-label-color;
      }
    }

    &--disabled {
      cursor: default;
      outline-color: tokens.$oc-component-form-disabled-border-color;
      background-color: tokens.$oc-component-form-disabled-background-color;

      .text-field__input {
        color: tokens.$oc-component-form-disabled-input-color;
      }

      .text-field__label {
        cursor: default;
        background-color: tokens.$oc-component-form-disabled-background-color;
        color: tokens.$oc-component-form-disabled-label-color;
      }
    }

    .text-field__prefix-text,
    .text-field__prefix-icon {
      grid-area: prefix;
      transition: $transition;
      opacity: 0;
      margin-right: $gap;
    }

    .text-field__suffix-text,
    .text-field__suffix-icon {
      grid-area: suffix;
      transition: $transition;
      opacity: 0;
      margin-left: $gap;
    }

    .text-field__prefix-text,
    .text-field__suffix-text {
      font: tokens.$oc-component-form-field-input-font;
      user-select: none;
    }

    &__input:autofill, /*                                                        */
    &:active:not(.text-field--disabled) .text-field__input, /*                                      */
    &:focus-within .text-field__input,
    &__input:not(:placeholder-shown) {
      &::placeholder,
      ~ .text-field__suffix-icon,
      ~ .text-field__suffix-text,
      ~ .text-field__prefix-icon,
      ~ .text-field__prefix-text {
        opacity: 1;
      }

      ~ .text-field__label {
        top: -0.5rem;
        left: tokens.$oc-component-form-field-floating-label-spacing-left;
        padding: 0 tokens.$oc-component-form-field-floating-label-inner-spacing-x;
        font: tokens.$oc-component-form-field-floating-label-font;
      }
    }

    /*                           */
    &__input {
      &:autofill {
        -webkit-box-shadow: 0 0 0 50px $autofill-background-color inset;
        -webkit-text-fill-color: tokens.$oc-component-form-field-input-color;
      }
    }

    /*                                                                                 */
    &:has(.text-field__input:autofill) {
      background: $autofill-background-color;
    }
  }
</style>
